<template>
  <article class="card print-area p-5 sm:p-8 lg:p-10 print:border-0 print:p-0 print:shadow-none">
    <!-- Capa -->
    <header class="mb-7 border-b-[3px] border-blue-900 pb-5 dark:border-blue-300">
      <p class="text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-700 dark:text-sky-400">Relatório de Gestão e Prestação de Contas</p>
      <h2 class="mt-1.5 text-2xl font-bold text-blue-900 dark:text-blue-200 sm:text-3xl">Centro Carioca de Diagnóstico e Tratamento por Imagem</h2>
      <p class="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
        Competência {{ rotuloCompetencia(competencia) }}
        <template v-if="capa.campos.periodo"> · período de referência {{ capa.campos.periodo }}</template>
        <template v-if="capa.campos.resp_tecnico"> · responsável técnico {{ capa.campos.resp_tecnico }}</template>
        <template v-if="capa.campos.elaboracao"> · elaboração {{ capa.campos.elaboracao }}</template>
      </p>
    </header>

    <!-- Resumo executivo -->
    <section class="mb-8 break-inside-avoid">
      <h3 class="text-lg font-bold text-blue-900 dark:text-blue-200">Resumo Executivo</h3>
      <p class="mb-3 text-xs text-gray-500 dark:text-gray-400">Números puxados das respostas de cada área</p>
      <p v-if="capa.campos.destaques" class="mb-2 max-w-[70ch] whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200">{{ capa.campos.destaques }}</p>
      <p v-else class="text-sm italic text-gray-500 dark:text-gray-400">A Coordenação ainda não escreveu os destaques do mês.</p>

      <div v-if="kpisResumo.length" class="my-3 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
        <div v-for="[label, valor] in kpisResumo" :key="label" class="rounded-lg bg-slate-100 px-3 py-2.5 dark:bg-gray-900/60 print:border print:border-gray-200">
          <span class="block min-h-[2rem] text-[11px] leading-tight text-gray-500 dark:text-gray-400">{{ label }}</span>
          <b class="text-xl font-semibold tabular-nums text-blue-900 dark:text-blue-200">{{ valor }}</b>
        </div>
      </div>

      <template v-if="pontosAtencao.length">
        <h4 class="mb-1.5 mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">Pontos de atenção</h4>
        <div class="table-wrap">
          <table class="w-full text-sm">
            <thead>
              <tr class="bg-blue-900 text-left text-[11px] uppercase tracking-wide text-white print:[-webkit-print-color-adjust:exact] print:[print-color-adjust:exact]">
                <th class="px-2.5 py-1.5">Status</th><th class="px-2.5 py-1.5">Ponto de atenção</th><th class="px-2.5 py-1.5 text-right">Resultado</th><th class="px-2.5 py-1.5 text-right">Meta ou prazo</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(p, i) in pontosAtencao" :key="i" class="border-b border-gray-200 align-top last:border-0 dark:border-gray-700">
                <td class="px-2.5 py-1.5">{{ p.status || '' }}</td>
                <td class="px-2.5 py-1.5">{{ p.desc }}</td>
                <td class="px-2.5 py-1.5 text-right tabular-nums">{{ numBR(p.res) }}</td>
                <td class="px-2.5 py-1.5 text-right tabular-nums">{{ p.meta || '' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </section>

    <!-- Seções por área -->
    <section v-for="area in secoes" :key="area.id" class="mb-8 break-inside-avoid">
      <h3 class="text-lg font-bold text-blue-900 dark:text-blue-200">{{ area.sec }} · {{ area.titulo }}</h3>
      <p class="mb-3 text-xs text-gray-500 dark:text-gray-400">
        {{ area.area }}
        <template v-if="dadosDe(area).responsavel"> · {{ dadosDe(area).responsavel }}</template>
        <template v-if="dadosDe(area).atualizado"> · enviado em {{ formatarData(dadosDe(area).atualizado) }}</template>
      </p>

      <p v-if="!temConteudo(area)" class="text-sm italic text-gray-500 dark:text-gray-400">Sem dados enviados até agora.</p>

      <template v-else>
        <template v-for="bloco in area.blocos" :key="bloco.k || bloco.h">
          <!-- KPIs -->
          <div v-if="bloco.t === 'kpis' && kpisDe(area, bloco).length" class="my-3 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
            <div v-for="campo in kpisDe(area, bloco)" :key="campo.k" class="rounded-lg bg-slate-100 px-3 py-2.5 dark:bg-gray-900/60 print:border print:border-gray-200">
              <span class="block min-h-[2rem] text-[11px] leading-tight text-gray-500 dark:text-gray-400">{{ campo.l }}</span>
              <b class="text-xl font-semibold tabular-nums text-blue-900 dark:text-blue-200">{{ numBR(dadosDe(area).campos[campo.k]) }}</b>
            </div>
          </div>

          <!-- Texto -->
          <template v-else-if="bloco.t === 'texto' && dadosDe(area).campos[bloco.k]">
            <h4 class="mb-1.5 mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">{{ bloco.h }}</h4>
            <p class="mb-2 max-w-[70ch] whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200">{{ dadosDe(area).campos[bloco.k] }}</p>
          </template>

          <!-- Tabela -->
          <template v-else-if="bloco.t === 'tabela' && linhasComConteudo(bloco, dadosDe(area)).length">
            <h4 class="mb-1.5 mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">{{ bloco.h }}</h4>
            <div class="table-wrap">
              <table class="w-full text-sm">
                <thead>
                  <tr class="bg-blue-900 text-left text-[11px] uppercase tracking-wide text-white print:[-webkit-print-color-adjust:exact] print:[print-color-adjust:exact]">
                    <th v-for="col in bloco.cols" :key="col.k" class="px-2.5 py-1.5" :class="{ 'text-right': col.num }">{{ col.l }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(linha, i) in linhasComConteudo(bloco, dadosDe(area))" :key="i" class="border-b border-gray-200 align-top last:border-0 dark:border-gray-700">
                    <td v-for="col in bloco.cols" :key="col.k" class="px-2.5 py-1.5" :class="{ 'text-right tabular-nums': col.num }">
                      {{ col.num ? numBR(linha[col.k]) : (linha[col.k] ?? '') }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </template>
      </template>
    </section>
  </article>
</template>

<script setup>
import { computed, defineProps } from 'vue';
import { AREAS, AREA_ABERTURA } from './areas';
import {
  rotuloCompetencia, numBR, formatarData, vazio,
  linhasComConteudo, resumoExecutivo, progressoArea, abertura,
} from './prestacao';

const props = defineProps({
  competencia: { type: String, required: true },
  dados: { type: Object, required: true }, // { areaId: {responsavel, atualizado, campos} }
});

const capa = computed(() => abertura(props.dados));
const kpisResumo = computed(() => resumoExecutivo(props.dados));
const pontosAtencao = computed(() => (capa.value.campos.pontos_atencao || []).filter((l) => l.desc));
const secoes = AREAS.filter((a) => a.id !== AREA_ABERTURA);

const dadosDe = (area) => props.dados[area.id] || { responsavel: '', atualizado: '', campos: {} };
const temConteudo = (area) => progressoArea(area, props.dados[area.id]) > 0;
const kpisDe = (area, bloco) => bloco.campos.filter((c) => !vazio(dadosDe(area).campos[c.k]));
</script>
